import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import analytics from '../lib/analytics';
import { CheckoutAddonModal, PurchaseNavButton } from './CheckoutFlow';
import { BetaBadge, ComingSoonBanner } from './ComingSoon';
import {
  createPathwayCheckoutSession,
  getPathway,
  peekPendingPurchase,
  setPendingPurchase,
} from '../lib/pathways';
import { API_BASE as BACKEND } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${BACKEND}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.detail?.message || data.detail || res.statusText), { status: res.status });
  }
  return res.json();
};

// ── Pathway metadata ─────────────────────────────────────────────────────────
const PATHWAY_META = {
  'family-i130':      { name: 'Family-Based I-130',           icon: 'fa-users',          eb: false },
  'aos-i485':         { name: 'Adjustment of Status',          icon: 'fa-id-card',        eb: false },
  'perm-employer':    { name: 'PERM / Employer-Sponsored',     icon: 'fa-briefcase',      eb: true  },
  'eb2-niw':          { name: 'EB-2 National Interest Waiver', icon: 'fa-graduation-cap', eb: true  },
  'eb1a':             { name: 'EB-1A Extraordinary Ability',   icon: 'fa-trophy',         eb: true  },
  'asylum':           { name: 'Asylum & Humanitarian',         icon: 'fa-shield-alt',     eb: false },
  'f1-change-status': { name: 'F-1 / Change of Status',       icon: 'fa-university',     eb: false },
  'o1-nonimmigrant':  { name: 'O-1 Extraordinary Ability',    icon: 'fa-star',           eb: true  },
  'dv-lottery':       { name: 'Diversity Visa (DV Lottery)',   icon: 'fa-globe',          eb: false },
};

// ── Small helper components ───────────────────────────────────────────────────
const StrengthBadge = ({ level }) => {
  const cfg = {
    STRONG:           { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Strong' },
    MODERATE:         { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Moderate' },
    NEEDS_ATTENTION:  { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Needs Attention' },
  }[level] || { bg: 'bg-gray-100', text: 'text-gray-600', label: level || '—' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

const UrgencyBadge = ({ urgency, days }) => {
  const cfg = {
    critical: { bg: 'bg-red-100',    text: 'text-red-700',    icon: 'fa-exclamation-circle' },
    urgent:   { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'fa-exclamation-triangle' },
    warning:  { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'fa-clock' },
    ok:       { bg: 'bg-green-100',  text: 'text-green-700',  icon: 'fa-check-circle' },
  }[urgency] || { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'fa-clock' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <i className={`fas ${cfg.icon}`}></i>
      {days != null ? `${days}d` : urgency}
    </span>
  );
};

const PanelCard = ({ title, icon, children, action, comingSoon = false }) => (
  <div className="app-panel">
    <div className="flex items-center justify-between mb-4">
      <h2 className="flex items-center gap-2">
        {title}
        {comingSoon && <BetaBadge />}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

const EmptyState = ({ msg }) => (
  <p className="text-primary/50 text-sm text-center py-4">{msg}</p>
);

// ── Panel 1: Active Pathways ──────────────────────────────────────────────────
const ActivePathwaysPanel = ({ pathways }) => {
  const navigate = useNavigate();
  const [strengths, setStrengths] = useState({});

  useEffect(() => {
    pathways.forEach(async (pid) => {
      try {
        const data = await apiFetch(`/api/workflow/${pid}/strength`);
        setStrengths(prev => ({ ...prev, [pid]: data }));
      } catch {/* best-effort */}
    });
  }, [pathways]);

  if (!pathways.length) {
    return (
      <PanelCard title="Your Pathways" icon="fa-route">
        <EmptyState msg="No active pathways yet." />
        <div className="mt-3 text-center">
          <a href="/pricing" className="inline-block bg-accent text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-accent/90 transition-colors">
            Browse Pathways
          </a>
        </div>
      </PanelCard>
    );
  }

  return (
    <PanelCard title="Your Pathways" icon="fa-route">
      <div className="space-y-4">
        {pathways.map(pid => {
          const meta = PATHWAY_META[pid] || { name: pid, icon: 'fa-file-alt', eb: false };
          const str = strengths[pid];
          return (
            <div key={pid} className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
              <div className="w-9 h-9 bg-accent/15 rounded-full flex items-center justify-center flex-shrink-0">
                <i className={`fas ${meta.icon} text-accent text-sm`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-primary text-sm truncate">{meta.name}</span>
                  {str?.case_strength_level && <StrengthBadge level={str.case_strength_level} />}
                </div>
                {str?.case_strength_score != null && (
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-accent h-1.5 rounded-full transition-all" style={{ width: `${str.case_strength_score}%` }}></div>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate(`/workflow/${pid}`)}
                className="flex-shrink-0 bg-accent text-primary px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-accent/90 transition-colors"
              >
                Continue
              </button>
            </div>
          );
        })}
      </div>
    </PanelCard>
  );
};

// ── Panel 2: Deadlines ────────────────────────────────────────────────────────
const DeadlinesPanel = ({ pathways }) => {
  const [deadlines, setDeadlines] = useState([]);

  useEffect(() => {
    const load = async () => {
      const items = [];
      try {
        const data = await apiFetch('/api/me/deadlines');
        (data.deadlines || []).forEach((d) => {
          const days = typeof d.days_remaining === 'number'
            ? d.days_remaining
            : Math.ceil((new Date(d.deadline_date) - Date.now()) / 86400000);
          const urgency = days <= 7 ? 'critical' : days <= 14 ? 'urgent' : days <= 30 ? 'warning' : 'ok';
          items.push({
            label: d.label || d.deadline_type,
            days,
            urgency,
            key: d.id || `${d.deadline_type}-${d.pathway_id}-${d.deadline_date}`,
          });
        });
      } catch {/* deadlines API unavailable */}

      // Also surface RFE cases if not already in deadline_reminders
      try {
        const rfeCases = await apiFetch('/api/rfe');
        const list = Array.isArray(rfeCases) ? rfeCases : (rfeCases.cases || []);
        list.forEach((c) => {
          if (!c.response_deadline) return;
          const key = c.id;
          if (items.some((i) => i.key === key || String(i.key).includes(key))) return;
          const days = Math.ceil((new Date(c.response_deadline) - Date.now()) / 86400000);
          const urgency = days <= 7 ? 'critical' : days <= 14 ? 'urgent' : days <= 30 ? 'warning' : 'ok';
          items.push({ label: `RFE Response — ${c.status}`, days, urgency, key });
        });
      } catch {/* no RFE cases */}

      setDeadlines(items.sort((a, b) => a.days - b.days));
    };
    load();
  }, [pathways]);

  return (
    <PanelCard title="Upcoming Deadlines" icon="fa-calendar-alt" comingSoon>
      {deadlines.length === 0
        ? <EmptyState msg="No upcoming deadlines." />
        : (
          <ul className="space-y-2">
            {deadlines.map(d => (
              <li key={d.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-primary/80 truncate">{d.label}</span>
                <UrgencyBadge urgency={d.urgency} days={d.days} />
              </li>
            ))}
          </ul>
        )
      }
    </PanelCard>
  );
};

// ── Panel 3: Documents ────────────────────────────────────────────────────────
const DocumentsPanel = ({ pathways }) => {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    const load = async () => {
      const all = [];
      for (const pid of pathways) {
        try {
          const data = await apiFetch(`/api/documents/${pid}`);
          const list = Array.isArray(data) ? data : (data.documents || []);
          list.forEach(d => all.push({ ...d, pathway: pid }));
        } catch {/* no docs yet */}
      }
      setDocs(all);
    };
    if (pathways.length) load();
  }, [pathways]);

  const download = async (pathwayId, docId, doc) => {
    try {
      const token = localStorage.getItem('token');
      const isPdf = (doc?.mime_type || '').includes('pdf') || doc?.document_type === 'petition_pdf';
      const paths = isPdf
        ? [
            `/api/documents/${pathwayId}/${docId}/download/pdf`,
            `/api/documents/${pathwayId}/${docId}/download/docx`,
          ]
        : [
            `/api/documents/${pathwayId}/${docId}/download/docx`,
            `/api/documents/${pathwayId}/${docId}/download/pdf`,
          ];
      let res = null;
      for (const path of paths) {
        res = await fetch(`${BACKEND}${path}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) break;
      }
      if (!res || !res.ok) {
        throw new Error('Download failed');
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const fallback = isPdf ? `document_${docId}.pdf` : `document_${docId}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || fallback;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed: ' + e.message);
    }
  };

  return (
    <PanelCard title="Generated Documents" icon="fa-file-alt">
      {docs.length === 0
        ? <EmptyState msg="No documents generated yet. Complete a workflow to generate documents." />
        : (
          <ul className="space-y-2">
            {docs.map(d => {
              const meta = PATHWAY_META[d.pathway] || { name: d.pathway };
              const expiry = d.expires_at ? new Date(d.expires_at) : null;
              const daysLeft = expiry ? Math.ceil((expiry - Date.now()) / 86400000) : null;
              return (
                <li key={d.id || d.document_id} className="flex items-center justify-between gap-2 text-sm p-2 bg-primary/5 rounded-lg">
                  <div className="min-w-0">
                    <div className="text-primary/80 font-medium truncate">{d.document_type || d.generation_trigger || 'Document'}</div>
                    <div className="text-primary/50 text-xs">{meta.name}{daysLeft != null ? ` · expires in ${daysLeft}d` : ''}</div>
                  </div>
                  <button
                    onClick={() => download(d.pathway, d.id || d.document_id, d)}
                    className="flex-shrink-0 text-accent hover:text-accent/80 text-xs font-semibold"
                  >
                    <i className="fas fa-download mr-1"></i>Download
                  </button>
                </li>
              );
            })}
          </ul>
        )
      }
    </PanelCard>
  );
};

// ── Panel 4: RFE Cases ────────────────────────────────────────────────────────
const RFEPanel = ({ navigate }) => {
  const [cases, setCases] = useState(null);

  useEffect(() => {
    apiFetch('/api/rfe').then(setCases).catch(() => setCases([]));
  }, []);

  if (cases === null) return null; // hide while loading
  if (cases.length === 0) return null; // hide if no RFE activity

  return (
    <PanelCard
      title="RFE Cases"
      icon="fa-reply"
      comingSoon
      action={
        <button onClick={() => navigate('/rfe')} className="text-accent hover:text-accent/80 text-xs font-semibold">
          Manage RFEs →
        </button>
      }
    >
      <ul className="space-y-2">
        {cases.slice(0, 5).map(c => {
          const days = c.days_remaining;
          const urgency = days <= 7 ? 'critical' : days <= 14 ? 'urgent' : days <= 30 ? 'warning' : 'ok';
          return (
            <li key={c.id} className="flex items-center justify-between gap-2 text-sm p-2 bg-primary/5 rounded-lg">
              <div>
                <div className="text-primary/80 font-medium capitalize">{c.rfe_type} RFE</div>
                <div className="text-primary/50 text-xs capitalize">{c.status}</div>
              </div>
              <UrgencyBadge urgency={urgency} days={days} />
            </li>
          );
        })}
      </ul>
    </PanelCard>
  );
};

// ── Panel 5: Priority Date ────────────────────────────────────────────────────
const PriorityDatePanel = ({ pathways, navigate }) => {
  const [data, setData] = useState({});
  const [switchRecs, setSwitchRecs] = useState([]);
  const ebPathways = pathways.filter(pid => PATHWAY_META[pid]?.eb);

  useEffect(() => {
    if (!ebPathways.length) return;
    ebPathways.forEach(async pid => {
      try {
        const d = await apiFetch(`/api/priority-date/${pid}`);
        setData(prev => ({ ...prev, [pid]: d }));
      } catch {/* no data */}
    });
    apiFetch('/api/priority-date/switch-recommendations').then(setSwitchRecs).catch(() => {});
  }, [pathways]); // eslint-disable-line

  if (!ebPathways.length) return null;

  return (
    <PanelCard
      title="Priority Dates"
      icon="fa-calendar-check"
      comingSoon
      action={
        <button onClick={() => navigate('/priority-dates')} className="text-accent hover:text-accent/80 text-xs font-semibold">
          Full view →
        </button>
      }
    >
      {switchRecs.length > 0 && (
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm">
          <i className="fas fa-lightbulb text-yellow-600 mr-2"></i>
          <strong className="text-yellow-800">Pathway Switch Recommendation</strong>
          <p className="text-yellow-700 mt-1 text-xs">{switchRecs[0].recommendation_reason}</p>
          <button
            onClick={() => navigate('/priority-dates')}
            className="mt-2 bg-yellow-500 text-black px-3 py-1 rounded text-xs font-semibold hover:bg-yellow-400 transition-colors"
          >
            View Details — Switch at 50% off
          </button>
        </div>
      )}
      <div className="space-y-3">
        {ebPathways.map(pid => {
          const meta = PATHWAY_META[pid];
          const d = data[pid];
          return (
            <div key={pid} className="p-3 bg-primary/5 rounded-lg text-sm">
              <div className="font-semibold text-primary text-xs mb-1">{meta.name}</div>
              {d ? (
                <div className="text-primary/70 text-xs space-y-0.5">
                  <div>Current cutoff: <strong>{d.current_cutoff || '—'}</strong></div>
                  <div>Est. wait: <strong>{d.estimated_wait_months != null ? `${d.estimated_wait_months} months` : '—'}</strong></div>
                </div>
              ) : (
                <p className="text-primary/40 text-xs">Loading…</p>
              )}
            </div>
          );
        })}
      </div>
    </PanelCard>
  );
};

// ── Panel 6: Community Data ───────────────────────────────────────────────────
const CommunityPanel = ({ navigate }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiFetch('/api/community/free-tier').then(setData).catch(() => {});
  }, []);

  return (
    <PanelCard
      title="Community Insights"
      icon="fa-users"
      comingSoon
      action={
        <button onClick={() => navigate('/community')} className="text-accent hover:text-accent/80 text-xs font-semibold">
          View all →
        </button>
      }
    >
      {!data
        ? <EmptyState msg="Loading community data…" />
        : (
          <div className="space-y-2 text-sm">
            {data.approval_rates && Object.keys(data.approval_rates).length > 0
              ? Object.entries(data.approval_rates).slice(0, 3).map(([pathwayId, rate]) => (
                <div key={pathwayId} className="flex items-center justify-between">
                  <span className="text-primary/70 truncate text-xs">{PATHWAY_META[pathwayId]?.name || pathwayId}</span>
                  <span className="font-semibold text-accent text-xs">{(rate * 100).toFixed(0)}% approval</span>
                </div>
              ))
              : <EmptyState msg="No community data available yet." />
            }
          </div>
        )
      }
    </PanelCard>
  );
};

// ── Panel 8: Immigration Roadmap ──────────────────────────────────────────────
const RoadmapPanel = ({ navigate }) => {
  const [roadmap, setRoadmap] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    apiFetch('/api/profile/roadmap').then(setRoadmap).catch(() => {});
    apiFetch('/api/profile/').then(setProfile).catch(() => {});
  }, []);

  const profileFields = profile ? [
    profile.current_visa_type,
    profile.country_of_birth,
    profile.employment_history?.length,
    profile.education_history?.length,
  ].filter(Boolean).length : 0;
  const profilePct = profile ? Math.min(100, Math.round((profileFields / 4) * 100)) : 0;

  return (
    <PanelCard
      title="Immigration Roadmap"
      icon="fa-map-marked-alt"
      action={
        <button onClick={() => navigate('/profile')} className="text-accent hover:text-accent/80 text-xs font-semibold">
          Update Profile →
        </button>
      }
    >
      {/* Profile completion */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-primary/70">Profile completion</span>
          <span className="font-semibold text-accent">{profilePct}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div className="bg-accent h-1.5 rounded-full transition-all" style={{ width: `${profilePct}%` }}></div>
        </div>
        {profilePct < 100 && (
          <button
            onClick={() => navigate('/profile')}
            className="mt-2 text-xs text-accent underline hover:text-accent/80"
          >
            Complete your profile for better recommendations
          </button>
        )}
      </div>

      {/* Roadmap steps */}
      {!roadmap
        ? <EmptyState msg="Create or update your profile to generate your personalized roadmap." />
        : (
          <div className="space-y-3">
            {roadmap.next_action && (
              <p className="text-xs text-accent font-semibold">{roadmap.next_action}</p>
            )}
            {(roadmap.roadmap || []).slice(0, 4).map((step, i) => (
              <div key={step.step || i} className="flex gap-3 items-start">
                <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-accent">{i + 1}</div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-primary">{step.title}</div>
                  {step.description && (
                    <div className="text-xs text-primary/70 mt-0.5 line-clamp-2">{step.description}</div>
                  )}
                </div>
              </div>
            ))}
            {(roadmap.roadmap || []).length === 0 && (
              <EmptyState msg={roadmap.summary || 'Complete your profile to see personalized recommendations.'} />
            )}
          </div>
        )
      }
    </PanelCard>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePathways, setActivePathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [checkoutBanner, setCheckoutBanner] = useState(null);
  const [purchasePathway, setPurchasePathway] = useState(null);
  const [recommendedPathway, setRecommendedPathway] = useState(null);

  // Redirect admin users
  useEffect(() => {
    if (user?.is_admin) navigate('/admin/dashboard');
  }, [user, navigate]);

  const handleLogout = () => { logout(); navigate('/'); };

  const loadData = useCallback(async () => {
    try {
      const ent = await apiFetch('/api/me/entitlement');
      setActivePathways(ent?.active_pathways || []);
    } catch (e) {
      if (e.status !== 403) setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Resume pending purchase after eligibility / auth (?purchase=eb2-niw)
  useEffect(() => {
    if (loading) return;
    const fromQuery = searchParams.get('purchase');
    const pending = fromQuery || peekPendingPurchase();
    if (!pending) return;
    if (activePathways.includes(pending)) return;

    const pathway = getPathway(pending);
    if (pathway) {
      setRecommendedPathway(pathway);
      setPurchasePathway((current) => current || pathway);
      setPendingPurchase(pending);
    }
    if (fromQuery) {
      const next = new URLSearchParams(searchParams);
      next.delete('purchase');
      setSearchParams(next, { replace: true });
    }
  }, [loading, activePathways, searchParams, setSearchParams]);

  // Remember eligibility recommendation even without auto-opening modal
  useEffect(() => {
    const pending = peekPendingPurchase();
    if (pending && !activePathways.includes(pending)) {
      const pathway = getPathway(pending);
      if (pathway) setRecommendedPathway(pathway);
    }
  }, [activePathways]);

  const openPurchase = (pathwayId) => {
    const pathway = getPathway(pathwayId);
    if (!pathway) return;
    setPendingPurchase(pathwayId);
    setPurchasePathway(pathway);
  };

  const confirmPurchaseCheckout = async ({ attorney_review, aos_addon }) => {
    if (!purchasePathway) return;
    try {
      const url = await createPathwayCheckoutSession(purchasePathway.id, {
        attorney_review,
        aos_addon,
      });
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Checkout failed');
      setPurchasePathway(null);
    }
  };

  // Stripe Checkout return: success / canceled
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    const pathway = searchParams.get('pathway');
    const isSwitch = searchParams.get('switch') === 'true';

    if (!checkout) return;

    let cancelled = false;

    const clearQuery = () => {
      const next = new URLSearchParams(searchParams);
      ['checkout', 'session_id', 'pathway', 'switch'].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    };

    if (checkout === 'canceled') {
      setCheckoutBanner({
        type: 'canceled',
        message: isSwitch
          ? 'Pathway switch checkout was canceled. No charge was made.'
          : 'Checkout was canceled. No charge was made.',
      });
      clearQuery();
      return;
    }

    if (checkout !== 'success') return;

    setCheckoutBanner({
      type: 'pending',
      message: 'Payment received — confirming your purchase…',
    });

    const confirmPurchase = async () => {
      // Poll webhook-created entitlement (up to ~15s)
      let ready = false;
      if (sessionId) {
        for (let i = 0; i < 10; i++) {
          if (cancelled) return;
          try {
            const status = await apiFetch(
              `/api/billing/checkout/status?session_id=${encodeURIComponent(sessionId)}`
            );
            if (status.ready) {
              ready = true;
              break;
            }
          } catch {/* keep polling */}
          await new Promise((r) => setTimeout(r, 1500));
        }
      }

      try {
        await apiFetch('/api/user/purchases/sync', { method: 'POST' });
      } catch {/* ok */}
      if (cancelled) return;
      await loadData();

      analytics.trackPurchaseSuccess(pathway || (isSwitch ? 'pathway-switch' : 'pathway'), sessionId);

      setCheckoutBanner({
        type: ready ? 'success' : 'success_delayed',
        message: ready
          ? `Purchase confirmed${pathway ? ` for ${PATHWAY_META[pathway]?.name || pathway}` : ''}. Your pathway is unlocked.`
          : 'Payment received. If your pathway is not listed yet, wait a moment and tap Refresh — webhook confirmation can take a few seconds.',
        pathway,
      });
      clearQuery();
    };

    confirmPurchase();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, loadData]);

  const refreshPurchases = async () => {
    setRefreshing(true);
    try {
      await apiFetch('/api/user/purchases/sync', { method: 'POST' });
    } catch {/* 401 only if logged out */}
    await loadData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="app-shell flex items-center justify-center">
        <div className="text-[#D4A017] text-lg font-cormorant tracking-wide">
          Loading your dashboard…
        </div>
      </div>
    );
  }

  const ebPathways = activePathways.filter(pid => PATHWAY_META[pid]?.eb);

  return (
    <div className="app-shell">
      {/* Nav */}
      <nav className="app-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
            role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter') navigate('/'); }}
          >
            <span className="wordmark-mark" aria-hidden="true"></span>
            <span className="wordmark text-xl text-[#FFFFFF]">ImmigrateSmart</span>
          </div>
          <div className="nav-links-wrap flex items-center gap-3 sm:gap-5 flex-wrap">
            <PurchaseNavButton />
            <span className="text-white/80 text-sm hidden sm:inline">
              {user?.full_name || user?.email}
            </span>
            <button onClick={() => navigate('/profile')} className="text-white/90 hover:text-[#D4A017] transition-colors text-sm">
              Profile
            </button>
            <button onClick={() => navigate('/community')} className="text-white/90 hover:text-[#D4A017] transition-colors text-sm hidden sm:inline">
              Community
            </button>
            <button onClick={() => navigate('/rfe')} className="text-white/90 hover:text-[#D4A017] transition-colors text-sm hidden sm:inline">
              RFE
            </button>
            {ebPathways.length > 0 && (
              <button onClick={() => navigate('/priority-dates')} className="text-white/90 hover:text-[#D4A017] transition-colors text-sm hidden md:inline">
                Priority Dates
              </button>
            )}
            <button onClick={handleLogout} className="text-white/90 hover:text-[#D4A017] transition-colors text-sm">
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 sm:mb-10 border-b border-white/10 pb-6">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4A017] mb-2 font-semibold">Case workspace</p>
            <h1 className="text-3xl sm:text-4xl text-[#FFFFFF] font-cormorant font-semibold">Dashboard</h1>
          </div>
          <button
            onClick={refreshPurchases}
            disabled={refreshing}
            className="px-4 py-2.5 bg-[#D4A017] text-[#222226] text-xs font-semibold tracking-widest uppercase hover:bg-[#E8B41F] transition-colors disabled:opacity-50"
          >
            {refreshing ? 'Syncing…' : 'Sync purchases'}
          </button>
        </div>

        {checkoutBanner && (
          <div
            className={`px-4 py-3 mb-6 text-sm flex items-start justify-between gap-3 border ${
              checkoutBanner.type === 'canceled'
                ? 'bg-[#D4A017]/10 border-[#D4A017]/40 text-[#FFFFFF]'
                : checkoutBanner.type === 'pending'
                  ? 'bg-white/5 border-white/20 text-[#FFFFFF]'
                  : 'bg-white/5 border-[#D4A017]/50 text-[#FFFFFF]'
            }`}
            data-testid="checkout-banner"
          >
            <span>{checkoutBanner.message}</span>
            <button
              type="button"
              className="opacity-70 hover:opacity-100"
              onClick={() => setCheckoutBanner(null)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {error && (
          <div className="border border-red-400/40 text-red-200 px-4 py-3 mb-6 text-sm">
            {error}
          </div>
        )}

        {/* No entitlements CTA */}
        {activePathways.length === 0 && (
          <div className="border border-[#D4A017]/35 bg-[#D4A017]/5 px-6 py-8 mb-8">
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#D4A017] mb-2 font-semibold">Get started</p>
            <h3 className="font-cormorant text-2xl text-[#FFFFFF] mb-2">
              {recommendedPathway
                ? `Continue to ${recommendedPathway.name}`
                : 'No active pathways yet'}
            </h3>
            <p className="text-white/80 text-sm mb-5 max-w-xl">
              {recommendedPathway
                ? `Purchase ${recommendedPathway.name} (${recommendedPathway.price}) to unlock your workflow, documents, and evidence tools.`
                : 'Purchase a pathway to unlock your workflow, documents, and evidence tools. Eligibility check is optional — buy directly anytime.'}
            </p>
            <div className="flex gap-3 flex-wrap">
              {recommendedPathway ? (
                <button
                  type="button"
                  onClick={() => openPurchase(recommendedPathway.id)}
                  className="inline-block bg-[#D4A017] text-[#222226] px-5 py-3 text-xs font-bold tracking-widest uppercase hover:bg-[#E8B41F] transition-colors"
                >
                  Purchase {recommendedPathway.name} — {recommendedPathway.price}
                </button>
              ) : (
                <a href="/pricing" className="inline-block bg-[#D4A017] text-[#222226] px-5 py-2.5 text-xs font-semibold tracking-widest uppercase hover:bg-[#E8B41F] transition-colors">
                  Browse &amp; Purchase Pathways
                </a>
              )}
              <a href="/pricing" className="inline-block border border-white/30 text-[#FFFFFF] px-5 py-2.5 text-xs font-semibold tracking-widest uppercase hover:border-[#D4A017] transition-colors">
                All 9 Pathways
              </a>
              <a href="/eligibility" className="inline-block border border-white/30 text-[#FFFFFF] px-5 py-2.5 text-xs font-semibold tracking-widest uppercase hover:border-[#D4A017] transition-colors">
                Check Eligibility
              </a>
            </div>
          </div>
        )}

        {purchasePathway && (
          <CheckoutAddonModal
            pathway={purchasePathway}
            onClose={() => setPurchasePathway(null)}
            onConfirm={confirmPurchaseCheckout}
          />
        )}

        <ComingSoonBanner dark>
          Public launch includes pathway workflow, profile, eligibility, and generated documents.
          Case strength, deadlines, RFE, community data, priority dates, and attorney review are visible in beta — not sold as finished product.
        </ComingSoonBanner>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column — wider */}
          <div className="lg:col-span-2 space-y-6">
            <ActivePathwaysPanel pathways={activePathways} />
            {activePathways.length > 0 && <DocumentsPanel pathways={activePathways} />}
            <RFEPanel navigate={navigate} />
            {ebPathways.length > 0 && <PriorityDatePanel pathways={activePathways} navigate={navigate} />}
            <RoadmapPanel navigate={navigate} />
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {activePathways.length > 0 && <DeadlinesPanel pathways={activePathways} />}
            <CommunityPanel navigate={navigate} />

            {/* Quick links */}
            <div className="app-panel">
              <h2 className="mb-4">Quick actions</h2>
              <div className="space-y-1">
                <button
                  onClick={() => navigate('/profile')}
                  className="w-full text-left px-0 py-2.5 border-b border-[#222226]/08 text-sm text-[#222226]/80 hover:text-[#D4A017] transition-colors"
                >
                  Update profile
                </button>
                <button
                  onClick={() => navigate('/rfe')}
                  className="w-full text-left px-0 py-2.5 border-b border-[#222226]/08 text-sm text-[#222226]/80 hover:text-[#D4A017] transition-colors"
                >
                  Upload RFE notice
                </button>
                <button
                  onClick={() => navigate('/community')}
                  className="w-full text-left px-0 py-2.5 border-b border-[#222226]/08 text-sm text-[#222226]/80 hover:text-[#D4A017] transition-colors"
                >
                  Community data
                </button>
                {ebPathways.length > 0 && (
                  <button
                    onClick={() => navigate('/priority-dates')}
                    className="w-full text-left px-0 py-2.5 border-b border-[#222226]/08 text-sm text-[#222226]/80 hover:text-[#D4A017] transition-colors"
                  >
                    Priority dates
                  </button>
                )}
                <a
                  href="/#attorney-assist"
                  className="w-full text-left block px-0 py-2.5 text-sm text-[#222226]/80 hover:text-[#D4A017] transition-colors"
                >
                  Attorney assist
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
